import { isArray, isEmpty } from 'lodash-es';

import {
  DOCUMENT_DESCRIPTION_BY_TYPE,
  DOCUMENT_DOWNLOAD_PATH_BY_TYPE,
  DOCUMENT_ICON_BY_TYPE,
  DOCUMENT_INACTIVE_STATUSES,
  DOCUMENT_SECTION_DESCRIPTION,
  DOCUMENT_SECTION_TEXT,
  DOCUMENT_STATUS_STEP_AND,
  DOCUMENT_STATUS_STEP_OR,
  DOCUMENT_SUBTITLE_BY_TYPE,
  DOCUMENT_TYPE_HTML,
  GROUPED_CAYMAN_DOCUMENTS,
} from '@/constants/document';
import { getTenantConfigs } from '@/constants/tenant-configs';

import { createMockDocument } from '@/test-utils/createMock';

import {
  Document,
  DocumentSection,
  DocumentStatus,
  DocumentType,
  RequiredCondition,
  RequiredDocument,
  RequiredDocumentGroup,
  RequiredDocumentSection,
  SigningDocument,
  SigningFormType,
} from '@/types/document';
import { Language } from '@/types/i18n';
import {
  EmploymentStatus,
  Profession,
  User,
  UserAccountType,
} from '@/types/user';

import {
  isCaymanUser,
  isEmploymentInfoCollected,
  isExistingCustomer,
  isGrenadaUser,
  isMinorUser,
  isNationalUser,
  isRemediationCustomer,
} from './user';

export function getUserDocumentsBySection(documents: Document[]) {
  const sortedDocs = [...documents].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const documentsBySection: Record<string, Document[]> = {};
  const documentByDocType: Record<string, Record<string, Document>> = {};

  for (const doc of sortedDocs) {
    if (!documentsBySection[doc.type]) {
      documentsBySection[doc.type] = [];
    }
    documentsBySection[doc.type].push(doc);

    if (doc.docType) {
      documentByDocType[doc.type] = documentByDocType[doc.type] || {};

      if (!documentByDocType[doc.type][doc.docType]) {
        documentByDocType[doc.type][doc.docType] = doc;
      }

      const currentDoc = documentByDocType[doc.type][doc.docType];
      if (currentDoc.status !== doc.status) {
        documentByDocType[doc.type][doc.docType] = getHigherStatusDocument([
          doc,
          currentDoc,
        ]);
      }
    }
  }

  return { documentsBySection, documentByDocType };
}

export function getRequiredDocumentBySection(
  sections: RequiredDocumentSection[],
  user: User,
) {
  const requiredDocumentBySection: Partial<
    Record<DocumentSection, Partial<Record<DocumentType, RequiredDocument>>>
  > = {};

  sections.forEach(section => {
    const docs = isArray(section.docs) ? section.docs : [];
    // Get all documents of the section that were uploaded by the user
    const sectionDocuments = (user.documents ?? []).filter(
      item => item.type === section.type,
    );
    const documentByType = groupDocuments(sectionDocuments);
    docs.forEach(typeGroup => {
      requiredDocumentBySection[section.type] ??= {};

      typeGroup.split('|').forEach(typeText => {
        const type = typeText as DocumentType;
        const isNational = isNationalUser(
          user,
          getTenantConfigs(user.tenant)?.isoCode,
        );

        const descriptionObj =
          DOCUMENT_DESCRIPTION_BY_TYPE[type]?.[user.tenant] ||
          DOCUMENT_DESCRIPTION_BY_TYPE[type]?.default;
        const descriptionKey = isNational ? 'national' : 'nonNational';
        const description =
          typeof descriptionObj === 'string'
            ? descriptionObj
            : (descriptionObj?.[descriptionKey] ?? '');

        let docStatus = DocumentStatus.Pending;
        const entry = documentByType[type];

        if (
          entry?.status != null &&
          !isUnconfirmedAdditionalIdDocument(entry)
        ) {
          docStatus = entry.status;
        }

        const lang = (user.language ?? Language.English) as Language;
        const downloadPaths = DOCUMENT_DOWNLOAD_PATH_BY_TYPE[type]?.[lang];

        requiredDocumentBySection[section.type]![type] = {
          name: type,
          displayHTML: DOCUMENT_TYPE_HTML[type],
          subtitle:
            DOCUMENT_SUBTITLE_BY_TYPE[type]?.[user.tenant] ??
            DOCUMENT_SUBTITLE_BY_TYPE[type]?.default,
          description,
          icon: DOCUMENT_ICON_BY_TYPE[type] ?? 'file-text',
          status: docStatus,
          downloadPath: downloadPaths?.[user.tenant] ?? downloadPaths?.default,
        };
      });
    });
  });

  return requiredDocumentBySection;
}

const CONFIRMATION_GATED_ADDITIONAL_ID_SECTIONS = [
  DocumentSection.SecondID,
  DocumentSection.SecondCitizenshipID,
];

export function isConfirmationGatedAdditionalIdSection(
  section?: DocumentSection | string,
) {
  return CONFIRMATION_GATED_ADDITIONAL_ID_SECTIONS.includes(
    section as DocumentSection,
  );
}

export function isUnconfirmedAdditionalIdDocument(document?: Document) {
  return (
    !!document &&
    isConfirmationGatedAdditionalIdSection(document.type) &&
    document.infoConfirmed !== true
  );
}

export function groupLeaseAndLetterAsOther(
  user: User | undefined,
  document?: RequiredDocument,
): RequiredDocument | undefined {
  if (!user || !document || !isCaymanUser(user)) return document;

  return {
    ...document,
    groupedAs: DocumentType.Other,
    groupedTypes: [
      DocumentType.LeaseAgreement,
      DocumentType.LetterOfEmployment,
    ],
    name: DocumentType.Other,
    displayHTML: DOCUMENT_TYPE_HTML[DocumentType.Other],
    description: DOCUMENT_SUBTITLE_BY_TYPE[DocumentType.Other]?.[user.tenant],
    icon: DOCUMENT_ICON_BY_TYPE[DocumentType.Other] ?? 'file-text',
    status:
      getHigherStatus(
        user.documents
          .filter(item => item.docType === DocumentType.Other)
          .map(item => item.status),
      ) || DocumentStatus.Pending,
  };
}

const getPOEDocumentSectionDetails = (
  requiredSection: RequiredDocumentSection,
  user: User,
  defaultProps: { displayText?: string; description?: string },
) => {
  if (
    user.employmentDetailed?.employmentStatus === EmploymentStatus.Employed &&
    !user.employmentDetailed?.employerCountry
  ) {
    return {
      ...requiredSection,
      ...defaultProps,
      status: DocumentStatus.Pending,
    };
  }

  if (
    user.employmentDetailed?.employmentStatus ===
      EmploymentStatus.SelfEmployed &&
    user.employmentDetailed?.riskSectorFlag === false &&
    !user.employmentDetailed?.employerCountry
  ) {
    return {
      ...requiredSection,
      ...defaultProps,
      status: DocumentStatus.Pending,
    };
  }

  if (isEmploymentInfoCollected(user) && !requiredSection.docs?.length) {
    let calculatedStatus: DocumentStatus = DocumentStatus.InReview;
    if (
      user.employmentDetailed?.employmentStatus ===
        EmploymentStatus.Unemployed &&
      (getTenantConfigs(user.tenant)?.autoApproveUnemployedUsers ||
        getTenantConfigs(
          user.tenant,
        )?.autoApproveUnemployedProfessions?.includes(
          user.employmentDetailed.employmentDesc as Profession,
        ))
    ) {
      calculatedStatus = DocumentStatus.Approved;
    }

    if (user.riskSectorFlag) {
      calculatedStatus = DocumentStatus.Approved;
    }

    return {
      ...requiredSection,
      ...defaultProps,
      status: requiredSection.status ?? calculatedStatus,
    };
  }
};

// TODO: Refactor this function
export function getRequiredDocumentSectionDetails(
  requiredSection: RequiredDocumentSection,
  requiredDocumentByType: Record<string, RequiredDocument>,
  user: User,
): RequiredDocumentSection {
  const normalizedSection = {
    ...requiredSection,
    docs: isArray(requiredSection.docs) ? requiredSection.docs : [],
  };
  const sectionType = normalizedSection.type as string;
  const isSecondIdSection = sectionType === DocumentSection.SecondID;
  const defaultProps = {
    displayText:
      (isSecondIdSection ? 'documentSection.Second_ID.title' : undefined) ??
      DOCUMENT_SECTION_TEXT[normalizedSection.type]?.[user.tenant] ??
      DOCUMENT_SECTION_TEXT[normalizedSection.type]?.default,
    description: DOCUMENT_SECTION_DESCRIPTION[normalizedSection.type],
  };

  const activeIdentityDoc = findActiveDoc(
    user.documents,
    DocumentSection.Identity,
  );
  const identityDocType = activeIdentityDoc?.docType;

  if (isMinorUser(user)) {
    const isAddressSharedWithAdult =
      sectionType === DocumentSection.ProofOfAddress &&
      user.addressDetailed?.sameAsAdult === true;
    const isProofOfEmployment =
      sectionType === DocumentSection.ProofOfEmployment;

    if (isAddressSharedWithAdult || isProofOfEmployment) {
      return {
        ...normalizedSection,
        ...defaultProps,
        status: DocumentStatus.Approved,
      };
    }
  }

  if (isExistingCustomer(user) && !isMinorUser(user)) {
    if (!user.clientReviewed) {
      return {
        ...normalizedSection,
        ...defaultProps,
        status: DocumentStatus.Pending,
      };
    }

    // If existing customer's Personal Info is reviewed, and there is no required docs, set status to approved
    // If section is Proof of Address but user is not required to submit POA, set status to approved
    // If section is Proof of Employment but user is not required to submit POE, set status to approved
    if (
      (user.clientReviewed && requiredSection.docs.length === 0) ||
      shouldApprovePOA(user, sectionType) ||
      shouldApprovePOE(user, sectionType)
    ) {
      return {
        ...normalizedSection,
        ...defaultProps,
        status: DocumentStatus.Approved,
      };
    }
  }

  if (
    isRemediationCustomer(user) &&
    (requiredSection.docs.length === 0 ||
      shouldApprovePOA(user, sectionType) ||
      shouldApprovePOE(user, sectionType))
  ) {
    return {
      ...requiredSection,
      ...defaultProps,
      status: DocumentStatus.Approved,
    };
  }

  // #region Proof of Employment Section Details
  const poeSectionDetails = getPOEDocumentSectionDetails(
    normalizedSection,
    user,
    defaultProps,
  );
  if (sectionType === DocumentSection.ProofOfEmployment && poeSectionDetails) {
    return poeSectionDetails;
  }
  // #endregion Proof of Employment Section Details

  const orOfDocsGroups: RequiredDocumentGroup[] = [];
  const allOfDocs: RequiredDocumentGroup = {
    condition: RequiredCondition.And,
    items: [],
    status: DocumentStatus.Pending,
  };

  const shouldFilterOutDocTypeForGrenada = (
    docType: string,
    identityDocType: string | undefined,
  ) => {
    if (!isGrenadaUser(user)) return false;
    return (
      (identityDocType === DocumentType.IdentityDriverLicense &&
        docType === DocumentType.DriverLicense) ||
      (identityDocType === DocumentType.DriverLicense &&
        docType === DocumentType.IdentityDriverLicense)
    );
  };

  const shouldFilterOutDocType = (docType: string) => {
    if (!isSecondIdSection) return false;
    if (docType === identityDocType) return true;
    return shouldFilterOutDocTypeForGrenada(docType, identityDocType);
  };
  normalizedSection.docs.forEach(typeGroup => {
    if (!typeGroup.includes('|')) {
      if (shouldFilterOutDocType(typeGroup)) {
        return;
      }
      if (requiredDocumentByType[typeGroup]) {
        allOfDocs.items.push(requiredDocumentByType[typeGroup]);
      }
    } else {
      const typeGroupItems = typeGroup
        .split('|')
        // Filter out identity doc type for SecondID section
        .filter(item => !shouldFilterOutDocType(item));
      if (typeGroupItems.length === 0) {
        return;
      }

      const isLeaseOrLetter = typeGroupItems.some(item =>
        GROUPED_CAYMAN_DOCUMENTS.includes(item as DocumentType),
      );

      const orOfDocs = typeGroupItems
        .map(typeText => requiredDocumentByType[typeText])
        .filter((item): item is RequiredDocument => item !== undefined);
      if (
        isLeaseOrLetter &&
        normalizedSection.type == DocumentSection.ProofOfAddress &&
        isCaymanUser(user)
      ) {
        const leaseOrLetterDocs = orOfDocs.filter(item =>
          GROUPED_CAYMAN_DOCUMENTS.includes(item.name),
        );
        const otherDocs = orOfDocs.filter(
          item => !GROUPED_CAYMAN_DOCUMENTS.includes(item.name),
        );

        const allItems = [...otherDocs];
        if (leaseOrLetterDocs.length > 0) {
          allItems.push({
            ...leaseOrLetterDocs[0],
            groupedAs: DocumentType.Other,
            groupedTypes: GROUPED_CAYMAN_DOCUMENTS,
            name: DocumentType.Other,
            displayHTML: DOCUMENT_TYPE_HTML[DocumentType.Other],
            subtitle:
              DOCUMENT_SUBTITLE_BY_TYPE[DocumentType.Other]?.[user.tenant],
            icon: DOCUMENT_ICON_BY_TYPE[DocumentType.Other] ?? 'file-text',
            status:
              getHigherStatus(
                user.documents
                  .filter(item => item.docType === DocumentType.Other)
                  .map(item => item.status),
              ) || DocumentStatus.Pending,
          });
        }

        orOfDocsGroups.push({
          condition: RequiredCondition.Or,
          items: allItems,
          status: getHigherStatus(allItems.map(item => item.status)),
        });
      } else {
        orOfDocsGroups.push({
          condition: RequiredCondition.Or,
          items: orOfDocs,
          status: getHigherStatus(orOfDocs.map(item => item.status)),
        });
      }
    }
  });

  allOfDocs.status = getHigherStatus(
    allOfDocs.items.map(item => item.status),
    RequiredCondition.And,
  );
  const groups = [
    ...(allOfDocs.items.length ? [allOfDocs] : []),
    ...(orOfDocsGroups.length ? orOfDocsGroups : []),
  ];
  return {
    ...normalizedSection,
    ...defaultProps,
    groups,
    status:
      getHigherStatus(
        groups.map(item => item.status),
        RequiredCondition.And,
      ) || DocumentStatus.Pending,
  };
}

export function getHigherStatusDocument(
  documents: Document[],
  condition: RequiredCondition = RequiredCondition.Or,
) {
  let result: Document | undefined;

  for (const doc of documents) {
    if (!result) {
      result = doc;
      continue;
    }
    const currentStatus = result.status;
    const stepObj =
      condition === RequiredCondition.And
        ? DOCUMENT_STATUS_STEP_AND
        : DOCUMENT_STATUS_STEP_OR;
    const currentStep = stepObj[currentStatus] || 99;
    const step = stepObj[String(doc.status)] || 99;

    const difference = step - currentStep;

    if (difference < 0) {
      result = doc;
    }
  }

  return result as Document;
}

export function getHigherStatus(
  statuses: (DocumentStatus | undefined)[],
  condition: RequiredCondition = RequiredCondition.Or,
) {
  let result: DocumentStatus | undefined;

  for (const status of statuses) {
    if (!result) {
      result = status;
      continue;
    }
    const currentStatus = result;
    const stepObj =
      condition === RequiredCondition.And
        ? DOCUMENT_STATUS_STEP_AND
        : DOCUMENT_STATUS_STEP_OR;
    const currentStep = stepObj[currentStatus] || 99;
    const step = stepObj[String(status)] || 99;

    const difference = step - currentStep;

    if (difference < 0) {
      result = status;
    }
  }

  return result as DocumentStatus;
}

export function groupDocuments(
  documents: Document[],
  groupBy: 'sub-type' | 'form-id' = 'sub-type',
) {
  const documentByGroup: Record<string, Document> = {};

  for (const doc of documents) {
    const groupKey =
      (groupBy === 'sub-type' ? doc.docType : doc.adobeSignWidgetId) ??
      'untitled';

    if (!documentByGroup[groupKey]) {
      documentByGroup[groupKey] = doc;
    } else {
      documentByGroup[groupKey] = getHigherStatusDocument(
        [doc, documentByGroup[groupKey]],
        RequiredCondition.Or,
      );
    }
  }

  return documentByGroup;
}

export function getDocumentTypeStatus(
  documentsWithSameType: Document[] = [],
  requiredAmount = 1,
) {
  if (!documentsWithSameType.length) return DocumentStatus.Pending;

  const documentBySubType = groupDocuments(documentsWithSameType);
  const nonDuplicatedDocuments = Object.values(documentBySubType);

  if (requiredAmount < 1) return DocumentStatus.Pending;

  if (requiredAmount === 1) {
    return getHigherStatusDocument(nonDuplicatedDocuments, RequiredCondition.Or)
      ?.status;
  }

  while (nonDuplicatedDocuments.length < requiredAmount) {
    nonDuplicatedDocuments.push(
      createMockDocument({
        status: DocumentStatus.Pending,
      }),
    );
  }

  const status = getHigherStatusDocument(
    nonDuplicatedDocuments,
    RequiredCondition.And,
  )?.status;

  return [DocumentStatus.Signing].includes(status)
    ? DocumentStatus.Pending
    : status;
}

/** Signing section status when multiple Adobe forms are required (partial submission). */
export function getSigningFormsAggregateStatus(
  documentsWithSameType: Document[] = [],
  requiredFormCount = 1,
): DocumentStatus {
  if (requiredFormCount < 1) return DocumentStatus.Pending;

  const documentBySubType = groupDocuments(documentsWithSameType);
  const submitted = Object.values(documentBySubType);

  if (!submitted.length) return DocumentStatus.Pending;

  if (submitted.length < requiredFormCount) {
    const bestOfSubmitted = getHigherStatus(
      submitted.map(d => d.status),
      RequiredCondition.Or,
    );
    if (bestOfSubmitted === DocumentStatus.Rejected) {
      return DocumentStatus.Rejected;
    }
    if (
      [DocumentStatus.InReview, DocumentStatus.Signing].includes(
        bestOfSubmitted,
      )
    ) {
      return [DocumentStatus.Signing].includes(bestOfSubmitted)
        ? DocumentStatus.Pending
        : bestOfSubmitted;
    }
    return DocumentStatus.Pending;
  }

  return (
    getDocumentTypeStatus(documentsWithSameType, requiredFormCount) ??
    DocumentStatus.Pending
  );
}

export function getSignedDocuments(
  userDocuments: Document[] = [],
  formsToSign: SigningDocument[] = [],
) {
  if (!formsToSign.length) return [];

  const formIdsToSign = formsToSign.map(doc => doc.id);

  const result: Document[] = [];
  const signedFormKeys = new Set<string>();

  for (const doc of userDocuments) {
    if (
      !doc.adobeSignWidgetId ||
      !formIdsToSign.includes(doc.adobeSignWidgetId)
    )
      continue;

    if (
      [
        DocumentStatus.InReview,
        DocumentStatus.Signing,
        DocumentStatus.Approved,
      ].includes(doc.status)
    ) {
      const signedFormKey = `${doc.adobeSignWidgetId}|${doc.docType}`;
      if (signedFormKeys.has(signedFormKey)) continue;

      signedFormKeys.add(signedFormKey);
      result.push(doc);
    }
  }

  return result;
}

export type SigningDrawerDocumentLookups = {
  byDocTypeKey: Record<string, Document>;
  byAdobeSignWidgetId: Record<string, Document>;
};

export function getSigningDrawerListRowKey(form: SigningDocument): string {
  return String(
    form.docTypeWithAccountType ??
      form.docType ??
      form.code ??
      form.formName ??
      form.id,
  );
}

export function buildSigningDrawerDocumentLookups(
  documents: Document[] | undefined,
): SigningDrawerDocumentLookups {
  const byDocTypeKey: Record<string, Document> = {};
  const byAdobeSignWidgetId: Record<string, Document> = {};

  for (const doc of documents ?? []) {
    if (!doc?.adobeSignWidgetId) continue;

    const widgetId = `${String(doc.adobeSignWidgetId)}|${doc.docType}`;
    byAdobeSignWidgetId[widgetId] = byAdobeSignWidgetId[widgetId]
      ? getHigherStatusDocument([byAdobeSignWidgetId[widgetId], doc])
      : doc;

    const docTypeKey = String(doc.docType);
    byDocTypeKey[docTypeKey] = byDocTypeKey[docTypeKey]
      ? getHigherStatusDocument([byDocTypeKey[docTypeKey], doc])
      : doc;
  }

  return { byDocTypeKey, byAdobeSignWidgetId };
}

export function findUserDocumentForSigningForm(
  form: SigningDocument,
  lookups: SigningDrawerDocumentLookups,
): Document | undefined {
  const rowKey = getSigningDrawerListRowKey(form);
  const widgetIdKey = form.docTypeWithAccountType
    ? `${String(form.id)}|${form.docTypeWithAccountType}`
    : `${String(form.id)}|${form.docType}`;

  return (
    lookups.byDocTypeKey[rowKey] ??
    (form.id != null ? lookups.byAdobeSignWidgetId[widgetIdKey] : undefined)
  );
}

export function standardizeDocumentsToSign(
  signingDocs: SigningDocument[],
  accountTypes: UserAccountType[],
  tenantFormsOnEachAccountType: SigningFormType[],
): SigningDocument[] {
  if (isEmpty(signingDocs)) return [];
  // Split documents into those that need per-account copies and those that don't
  const formsOnEachAccountType = signingDocs.filter(doc =>
    tenantFormsOnEachAccountType.includes(doc.code),
  );

  const standardForms = signingDocs.filter(
    doc => !tenantFormsOnEachAccountType.includes(doc.code),
  );

  // Create account-specific forms
  const accountSpecificForms = createAccountSpecificForms(
    formsOnEachAccountType,
    accountTypes,
  );

  // Process standard forms
  const processedStandardForms = processStandardForms(standardForms);

  // Combine and sort all forms
  return [...accountSpecificForms, ...processedStandardForms].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

/**
 * Creates copies of forms for each account type
 */
function createAccountSpecificForms(
  forms: SigningDocument[],
  accountTypes: UserAccountType[],
): SigningDocument[] {
  if (!forms.length || !accountTypes.length) return [];

  return forms.flatMap(form =>
    accountTypes.map(accountType => {
      const accountTypeId = `${accountType.type}-${accountType.classCode}-${accountType.currency}`;

      return {
        ...form,
        name: `${form.name} (${accountType.currency} ${accountType.name})`,
        formName: `${form.name}|${accountTypeId}`,
        docType: form.code,
        docTypeWithAccountType: `${form.code}|${accountTypeId}`,
        prefillData: {
          acc_type: accountTypeId,
        },
      } as SigningDocument;
    }),
  );
}

/**
 * Processes standard forms that don't need account-specific copies
 */
function processStandardForms(forms: SigningDocument[]): SigningDocument[] {
  return forms.map(form => ({
    ...form,
    formName: form.name,
    docType: form.code,
  }));
}

const SUPPORTING_PERSONAL_DETAILS_DOCUMENTS = [
  DocumentSection.ProofOfAddress,
  DocumentSection.ProofOfEmployment,
];

export const findMissingPersonalDetailsDocuments = (
  currentSections: DocumentSection[],
) => {
  // Create a Set from currentSections for efficient lookup
  const currentSectionsSet = new Set(currentSections);

  // Filter to find values not present in currentSectionsSet
  const missingSections = SUPPORTING_PERSONAL_DETAILS_DOCUMENTS.filter(
    docSec => !currentSectionsSet.has(docSec),
  );

  return missingSections;
};
export function findActiveDoc(documents: Document[], section: DocumentSection) {
  return documents.find(
    doc =>
      doc.type === section && !DOCUMENT_INACTIVE_STATUSES.includes(doc.status),
  );
}

export function findLatestActiveDoc(
  documents: Document[],
  section: DocumentSection,
) {
  return documents
    .filter(
      doc =>
        doc.type === section &&
        !DOCUMENT_INACTIVE_STATUSES.includes(doc.status),
    )
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];
}

export function findActiveDocs(
  documents: Document[],
  sections: DocumentSection[],
) {
  return documents.filter(
    doc =>
      sections.includes(doc.type) &&
      !DOCUMENT_INACTIVE_STATUSES.includes(doc.status),
  );
}

export const hasValidAML4AOrAML4A_04_2025 = (user?: User): boolean =>
  (user?.documents || []).some(
    doc =>
      [SigningFormType.AML4A_04_2025, SigningFormType.AML4A].includes(
        doc.docType as SigningFormType,
      ) &&
      [DocumentStatus.InReview, DocumentStatus.Approved].includes(doc.status),
  );

export const hasValidAML6 = (user?: User): boolean =>
  (user?.documents || []).some(
    doc =>
      [SigningFormType.AML6].includes(doc.docType as SigningFormType) &&
      [DocumentStatus.InReview, DocumentStatus.Approved].includes(doc.status),
  );

export function getBackSideDocTypeName(frontDocType: string): string {
  return `${frontDocType}_Back`;
}

export function isIDWithoutPhoto(docType: DocumentType): boolean {
  return [
    DocumentType.BirthCertificate,
    DocumentType.CBBExtract,
    DocumentType.FamilyBook,
  ].includes(docType);
}

export function formatDocumentTypesList(
  docTypes: DocumentType[],
  translateFn: (key: string) => string,
): string {
  return docTypes.map(type => translateFn(`documentType.${type}`)).join(', ');
}

function shouldApprovePOA(user: User, sectionType: string) {
  return (
    sectionType === DocumentSection.ProofOfAddress &&
    user.addressDetailed?.poaRequired === false
  );
}

function shouldApprovePOE(user: User, sectionType: string) {
  return (
    sectionType === DocumentSection.ProofOfEmployment &&
    user.employmentDetailed?.poeRequired === false
  );
}

export function getIdentityDocumentCheckStatus(
  documents: Document[] = [],
  expectedDocumentCount = 3,
) {
  if (documents.length < expectedDocumentCount) {
    return DocumentStatus.Pending;
  }

  if (documents.length === expectedDocumentCount) {
    return DocumentStatus.Approved;
  }

  const isRejected = documents.some(
    doc => doc.status === DocumentStatus.Rejected,
  );

  if (isRejected) {
    return DocumentStatus.Rejected;
  }

  const hasInReview = documents.some(
    doc => doc.status === DocumentStatus.InReview,
  );
  if (hasInReview) {
    return DocumentStatus.InReview;
  }

  const countApproved = documents.filter(
    doc => doc.status === DocumentStatus.Approved,
  ).length;
  if (countApproved === expectedDocumentCount) {
    return DocumentStatus.Approved;
  }

  return DocumentStatus.Pending;
}

export function getMinorIdentityDocumentCheckStatus(
  documents: Document[] = [],
  expectedDocumentCount = 1,
) {
  const idDocuments = documents.filter(
    doc => doc.type === DocumentSection.Identity,
  );
  if (idDocuments.length < expectedDocumentCount) {
    return DocumentStatus.Pending;
  }

  const hasInReview = idDocuments.some(
    doc => doc.status === DocumentStatus.InReview,
  );
  if (hasInReview) {
    return DocumentStatus.InReview;
  }

  const countApproved = idDocuments.filter(
    doc => doc.status === DocumentStatus.Approved,
  ).length;
  if (countApproved === expectedDocumentCount) {
    return DocumentStatus.Approved;
  }

  const isRejected = idDocuments.some(
    doc => doc.status === DocumentStatus.Rejected,
  );

  if (isRejected) {
    return DocumentStatus.Rejected;
  }

  return DocumentStatus.Pending;
}
